import { type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { DotsHorizontalOutline } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode } from "@acto/taskpane/core/domain";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { useNumberFormatter } from "@acto/taskpane/field-sales-2/utils/hooks/useNumberFormatter";
import { useDateFormatter } from "@acto/taskpane/field-sales-2/utils/hooks/useDateFormatter";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { overlaidMenuClearance } from "@acto/taskpane/field-sales-2/styles/overlaidMenuClearance";
import { EM_DASH } from "./emDash";

const Card = styled("div")<{ clickable?: boolean }>(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[32]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glassLight,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderBright}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  overflow: "hidden",
  variants: [
    {
      props: { clickable: true },
      style: {
        cursor: "pointer",
        "&:hover": {
          background: theme.fieldSalesTwo.colors.surface.glassMedium,
        },
      },
    },
  ],
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  width: "100%",
  minWidth: 0,
}));

const HeaderText = styled("div")<{ clearMoreButton?: boolean }>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  minWidth: 0,
  variants: [
    {
      props: { clearMoreButton: true },
      style: {
        paddingRight: overlaidMenuClearance({
          menuRightInset: theme.fieldSalesTwo.spacing[14],
          menuButtonWidth: theme.fieldSalesTwo.spacing[50],
          gapToContent: theme.fieldSalesTwo.spacing[10],
          cardPaddingX: theme.fieldSalesTwo.spacing[20],
        }),
      },
    },
  ],
}));

const ExternalRef = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Name = styled("h3")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[136],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tighter,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "break-word",
}));

const HeaderMeta = styled("div")(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[16],
}));

const RevenueBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const RevenueRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const TrendChip = styled("span")<{ tone: "positive" | "negative" | "neutral" }>(({ theme }) => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: theme.fieldSalesTwo.spacing[72],
  height: theme.fieldSalesTwo.spacing[48],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[18],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  whiteSpace: "nowrap",
  variants: [
    {
      props: { tone: "positive" },
      style: {
        background: theme.fieldSalesTwo.colors.status.success.tint,
        color: theme.fieldSalesTwo.colors.status.success.main,
      },
    },
    {
      props: { tone: "negative" },
      style: {
        background: theme.fieldSalesTwo.colors.status.error.tint,
        color: theme.fieldSalesTwo.colors.status.error.main,
      },
    },
    {
      props: { tone: "neutral" },
      style: {
        background: theme.fieldSalesTwo.colors.neutral[200],
        color: theme.fieldSalesTwo.colors.neutral[800],
      },
    },
  ],
}));

const RevenueCol = styled("div")(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[2],
  minHeight: theme.fieldSalesTwo.spacing[64],
}));

const RevenuePeriodLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const RevenueValues = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: theme.fieldSalesTwo.spacing[6],
  whiteSpace: "nowrap",
}));

const RevenueValue = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const RevenuePrevious = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Story = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  width: "100%",
}));

const StoryCaption = styled("span")(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const StoryText = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.hairline,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "break-word",
}));

const PrimaryButton = styled(GlassButton)(({ theme }) => ({
  zIndex: 1,
  width: "100%",
  height: theme.fieldSalesTwo.spacing[50],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
}));

// A positioned z-index 0 layer paints above non-positioned siblings regardless of DOM order, so
// the overlay can lead the tab order and still receive taps.
const CardClickOverlay = styled("button")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 0,
  margin: 0,
  padding: 0,
  border: "none",
  borderRadius: "inherit",
  background: "transparent",
  cursor: "pointer",
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
}));

const MoreButton = styled(GlassButton)(({ theme }) => ({
  position: "absolute",
  top: theme.fieldSalesTwo.spacing[24],
  right: theme.fieldSalesTwo.spacing[14],
  zIndex: 1,
  width: theme.fieldSalesTwo.spacing[50],
  height: theme.fieldSalesTwo.spacing[50],
  padding: 0,
  color: theme.fieldSalesTwo.colors.neutral[850],
  // Invisible hit-area extension: near misses outside the button open the menu instead of the
  // card underneath.
  "&::before": {
    content: '""',
    position: "absolute",
    inset: `-${theme.fieldSalesTwo.spacing[10]}`,
    borderRadius: theme.fieldSalesTwo.radius.circular,
  },
}));

function resolveTrendTone(trend: number | null | undefined): "positive" | "negative" | "neutral" {
  if (trend == null || trend === 0) return "neutral";
  return trend > 0 ? "positive" : "negative";
}

interface CustomerCardProps {
  customer: Customer;
  /** Backend-resolved KPI window (rankedClients.dateRanges.current); no label when absent. */
  revenuePeriod?: { start: Date; end: Date } | null;
  headerMeta?: ReactNode;
  activityMeta?: ReactNode;
  onOpenCustomer?: () => void;
  onMoreActions?: () => void;
  primaryAction?: { label: ReactNode; onClick: () => void };
  className?: string;
}

export function CustomerCard({
  customer,
  revenuePeriod = null,
  headerMeta,
  activityMeta,
  onOpenCustomer,
  onMoreActions,
  primaryAction,
  className,
}: CustomerCardProps) {
  const t = useTranslations("fieldSalesTwo.customerCard");
  const comparisonMode = useComparisonModeContext();
  const previousPrefix =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("previousPeriodPrefix") : t("previousYearPrefix");
  const { locale } = useLocale();
  const formatNumber = useNumberFormatter();
  const { formatMonth } = useDateFormatter();

  const name = customer.name;
  const externalReference = customer.externalReference;
  const revenue = customer.revenueKpi?.totalValue ?? null;
  const previousRevenue = customer.revenueKpi?.previousTotalValue ?? null;
  const trend = customer.revenueKpi?.percentageChange ?? null;
  // Display-only sub-label ("Umsatz Jul 2025 – Jun 2026") next to the revenue
  // value. The window comes from the backend (rankedClients.dateRanges), so the
  // label can never drift from the range the KPIs were actually computed over.
  const revenuePeriodLabel = revenuePeriod
    ? t("revenuePeriod", {
        startMonth: formatMonth(revenuePeriod.start),
        startYear: revenuePeriod.start.getUTCFullYear(),
        endMonth: formatMonth(revenuePeriod.end),
        endYear: revenuePeriod.end.getUTCFullYear(),
      })
    : null;

  const formatRevenue = (value: number | null): string =>
    value == null ? EM_DASH : formatNumber({ number: value, type: "currency" });

  const shortStory = customer.getLocalizedShortStory(locale);
  const showRevenue = revenue != null || trend != null;

  return (
    <Card className={className} clickable={onOpenCustomer != null}>
      {onOpenCustomer && (
        <CardClickOverlay type="button" aria-label={`${name}, ${t("openDetails")}`} onClick={onOpenCustomer} />
      )}

      <Header>
        <HeaderText clearMoreButton={onMoreActions != null}>
          {externalReference && <ExternalRef>{externalReference}</ExternalRef>}
          <Name>{name}</Name>
        </HeaderText>
        {headerMeta && <HeaderMeta>{headerMeta}</HeaderMeta>}
      </Header>

      {showRevenue && (
        <RevenueBlock>
          <Divider />
          <RevenueRow>
            {trend != null && (
              <TrendChip tone={resolveTrendTone(trend)}>
                {formatNumber({
                  number: trend,
                  type: "percentage",
                  addPlusSign: true,
                })}
              </TrendChip>
            )}
            <RevenueCol>
              {revenuePeriodLabel && <RevenuePeriodLabel>{revenuePeriodLabel}</RevenuePeriodLabel>}
              <RevenueValues>
                <RevenueValue>{formatRevenue(revenue)}</RevenueValue>
                {previousRevenue != null && (
                  <RevenuePrevious>
                    {previousPrefix} {formatRevenue(previousRevenue)}
                  </RevenuePrevious>
                )}
              </RevenueValues>
            </RevenueCol>
          </RevenueRow>
          <Divider />
        </RevenueBlock>
      )}

      {activityMeta}

      {shortStory && (
        <Story>
          <StoryCaption>{t("storyCaption")}</StoryCaption>
          <StoryText>{shortStory}</StoryText>
        </Story>
      )}

      {primaryAction && (
        <PrimaryButton type="button" onClick={primaryAction.onClick}>
          {primaryAction.label}
        </PrimaryButton>
      )}

      {onMoreActions && (
        <MoreButton type="button" aria-label={t("moreActionsAriaLabel")} aria-haspopup="dialog" onClick={onMoreActions}>
          <DotsHorizontalOutline width={20} height={20} aria-hidden="true" />
        </MoreButton>
      )}
    </Card>
  );
}
